import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { TrustPilotWidget } from "@faire/retailer-visitor-shared/components/TestimonialCarousel/TrustPilotWidget";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { getTrustpilotRating } from "@faire/retailer-visitor-shared/serialized-data/getTrustpilotRating";
import { CASE_STUDIES, DEFAULT_CASE_STUDY, TRUST_PILOT_MINIMUM_RATING } from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isEnumKey } from "@faire/web--source/common/typescriptUtils";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContent } from "@faire/web--source/ui/ResponsiveContent";
import { trackBrandLeadsCaseStudySectionLinkedPdfClick } from "@faire/web-api--source/events/brandLeads/click/caseStudySectionLinkedPdf";
import { trackBrandLeadsCaseStudySectionView } from "@faire/web-api--source/events/brandLeads/view/caseStudySection";
import { trackBrandLeadsTrustPilotBadgeView } from "@faire/web-api--source/events/brandLeads/view/trustPilotBadge";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";
var BrandsLandingPageCaseStudiesBase = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var retailerCountry = useNationality().retailerCountry;
    var allCaseStudies = CASE_STUDIES(strictLocalize);
    var caseStudy = retailerCountry && isEnumKey(allCaseStudies, retailerCountry) ? allCaseStudies[retailerCountry] // We know that the key is valid from isEnumKey check above
     : DEFAULT_CASE_STUDY(strictLocalize);
    useEffect(function() {
        trackBrandLeadsCaseStudySectionView();
    }, []);
    var viewRef = useDoActionWhenVisible(trackBrandLeadsTrustPilotBadgeView);
    var FAIRE_TRUSTPILOT_RATING = getTrustpilotRating();
    return /*#__PURE__*/ _jsxs(Wrapper, {
        children: [
            /*#__PURE__*/ _jsx(ResponsiveContent, {
                mobile: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(HorizontalLine, {}),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "4x"
                        })
                    ]
                }),
                desktop: /*#__PURE__*/ _jsx(Spacer, {
                    height: {
                        mobileAndAbove: 0,
                        tabletAndAbove: "8x",
                        desktopAndAbove: "10x"
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Title, {
                variant: {
                    mobileAndAbove: "pageHeaderSerifRegular",
                    tabletAndAbove: "displayMSerifRegular",
                    desktopAndAbove: "displayLSerifRegular"
                },
                children: caseStudy.sectionTitle
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "2x",
                    tabletAndAbove: "5x"
                }
            }),
            /*#__PURE__*/ _jsx(ResponsiveContent, {
                mobile: /*#__PURE__*/ _jsx(MobileCaseStudyCard, {
                    brandInfo: caseStudy.brandInfo,
                    title: caseStudy.title,
                    image: caseStudy.mobileImage,
                    pdf: caseStudy.mobilePdf
                }),
                desktop: /*#__PURE__*/ _jsx(DesktopCaseStudyCard, {
                    brandInfo: caseStudy.brandInfo,
                    title: caseStudy.title,
                    image: caseStudy.desktopImage,
                    pdf: caseStudy.desktopPdf
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "5x",
                    tabletAndAbove: "7x"
                }
            }),
            /*#__PURE__*/ _jsx(ResponsiveContent, {
                mobile: null,
                desktop: FAIRE_TRUSTPILOT_RATING >= TRUST_PILOT_MINIMUM_RATING ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            ref: viewRef,
                            children: /*#__PURE__*/ _jsx(TrustPilotWidget, {
                                rating: FAIRE_TRUSTPILOT_RATING
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: {
                                mobileAndAbove: "7x",
                                desktopAndAbove: "10x"
                            }
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(Spacer, {
                    height: {
                        mobileAndAbove: 0,
                        desktopAndAbove: "3x"
                    }
                })
            })
        ]
    });
};
export var BrandsLandingPageCaseStudies = observer(BrandsLandingPageCaseStudiesBase);
var MobileCaseStudyCard = function(param) {
    var brandInfo = param.brandInfo, title = param.title, image = param.image, pdf = param.pdf;
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsxs(MobileCaseStudyWrapper, {
        children: [
            /*#__PURE__*/ _jsx(MobileCaseStudyImage, {
                src: optimizeImageUrl({
                    url: image
                }, isSSR, {
                    width: 312,
                    height: 146,
                    resizeMethod: "fit"
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "3x"
            }),
            /*#__PURE__*/ _jsx(CaseStudyBody, {
                brandInfo: brandInfo,
                title: title,
                pdf: pdf
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "2x"
            })
        ]
    });
};
var DesktopCaseStudyCard = function(param) {
    var brandInfo = param.brandInfo, title = param.title, image = param.image, pdf = param.pdf;
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsxs(DesktopCaseStudyWrapper, {
        children: [
            /*#__PURE__*/ _jsx(DesktopCaseStudyImage, {
                src: optimizeImageUrl({
                    url: image
                }, isSSR, {
                    width: 458,
                    height: 332,
                    resizeMethod: "fit"
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: {
                    mobileAndAbove: "6x",
                    desktopAndAbove: "8x"
                }
            }),
            /*#__PURE__*/ _jsx(CaseStudyBody, {
                brandInfo: brandInfo,
                title: title,
                pdf: pdf
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "2x"
            })
        ]
    });
};
var CaseStudyBody = function(param) {
    var brandInfo = param.brandInfo, title = param.title, pdf = param.pdf;
    return /*#__PURE__*/ _jsxs(CaseStudyBodyWrapper, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.subdued,
                variant: {
                    mobileAndAbove: "labelSansRegular",
                    tabletAndAbove: "paragraphSansRegular",
                    desktopAndAbove: "subheadingSansRegular"
                },
                children: brandInfo
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "1x",
                    tabletAndAbove: "2x"
                }
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: {
                    mobileAndAbove: "paragraphSansMedium",
                    tabletAndAbove: "subheadingSansRegular",
                    desktopAndAbove: "displaySSansRegular"
                },
                children: title
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "2x",
                    tabletAndAbove: "3x"
                }
            }),
            /*#__PURE__*/ _jsxs(EmbeddedLink, {
                linkTo: pdf,
                target: "_blank",
                onClick: function() {
                    trackBrandLeadsCaseStudySectionLinkedPdfClick();
                },
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraphSansRegular",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "xt9OAE",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Read the Case Study"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(StyledArrow, {
                        direction: "right"
                    })
                ]
            })
        ]
    });
};
var Wrapper = styled(Column).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-fc16de73-0"
})([
    "width:100%;",
    "{background-color:",
    ";padding:",
    ";}",
    "{background-color:",
    ";}"
], responsiveMediaQueries.mobile, Core.surface.inverse, getSpacing(0, "3x"), responsiveMediaQueries.tabletAndAbove, Decorative.tint.neutral);
var Title = styled(Typography).withConfig({
    componentId: "sc-fc16de73-1"
})([
    "",
    "{text-align:left;width:100%;}",
    "{width:824px;text-align:center;}"
], responsiveMediaQueries.mobile, responsiveMediaQueries.tabletAndAbove);
var DesktopCaseStudyWrapper = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-fc16de73-2"
})([
    "background-color:",
    ";padding:",
    ";",
    "{max-width:760px;}",
    "{max-width:980px;}"
], Core.surface.inverse, getSpacing("4x"), responsiveMediaQueries.tablet, responsiveMediaQueries.desktopAndAbove);
var MobileCaseStudyWrapper = styled(Column).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-fc16de73-3"
})([
    "border:1px solid ",
    ";border-radius:4px;max-width:312px;"
], Core.border.muted);
var DesktopCaseStudyImage = styled.img.withConfig({
    componentId: "sc-fc16de73-4"
})([
    "border-radius:4px;",
    "{width:320px;height:232px;}",
    "{width:458px;height:332px;}"
], responsiveMediaQueries.tablet, responsiveMediaQueries.desktopAndAbove);
var MobileCaseStudyImage = styled.img.withConfig({
    componentId: "sc-fc16de73-5"
})([
    "border-radius:4px 4px 0 0;width:312px;height:146px;"
]);
var CaseStudyBodyWrapper = styled(Column).withConfig({
    componentId: "sc-fc16de73-6"
})([
    "",
    "{padding:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing(0, "3x"));
var StyledArrow = styled(Arrow).withConfig({
    componentId: "sc-fc16de73-7"
})([
    "color:",
    ";height:20px;width:16px;margin-left:4px;"
], Core.text.default);
